/**
 * Central date/time formatter — all UI times render in CET (Europe/Berlin).
 *
 * Milestone: user request 2026-04-24 — "Время везде в UI должно быть показано
 * по CET". The dashboard used to mix UTC labels (chart axes, trace tables)
 * with the viewer's browser-local timezone (feedback cards, user activity),
 * which left operators second-guessing whether "14:32" meant UTC, Berlin, or
 * whatever laptop they opened the page on. SSOT is now `Europe/Berlin`.
 *
 * Design notes:
 *   - Backend still returns raw UTC ISO strings. No backend changes needed —
 *     conversion happens only at render time.
 *   - We display the label literal `"CET"` year-round even though the IANA
 *     zone flips to CEST in summer. User preference; simplification > format
 *     pedantry. If that ever needs to flip, bump `CET_LABEL` to dynamic.
 *   - Chart-bucket alignment: backend buckets are UTC-aligned
 *     (`date_trunc('day' | 'hour', timestamp)` in Postgres). Daily buckets at
 *     UTC midnight → 02:00 CET / 01:00 CET depending on DST — formatted as
 *     the CET calendar date that contains that instant, which keeps bars
 *     visually aligned with the user's perceived day. The occasional "the
 *     Jan 5 bar contains some Jan 4 UTC data at the seam" drift is invisible
 *     at dashboard resolution; we do not re-aggregate client-side.
 */

export const CET_TIMEZONE = 'Europe/Berlin';
export const CET_LABEL = 'CET';

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(opts);
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { ...opts, timeZone: CET_TIMEZONE });
    formatterCache.set(key, f);
  }
  return f;
}

function coerceDate(value: Date | string | number): Date | null {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/**
 * Extract CET calendar parts for a Date. Replacement for
 * `getUTCFullYear`/`getUTCMonth`/`getUTCDate`/`getUTCHours`/`getUTCMinutes`
 * when the intent is "the year/month/day/hour/minute a user in Berlin sees".
 */
export function getCetParts(value: Date | string | number): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
} | null {
  const d = coerceDate(value);
  if (!d) return null;
  const parts = getFormatter({
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short',
  }).formatToParts(d);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  const weekdayMap: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
  };
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour) % 24,
    minute: Number(map.minute),
    weekday: weekdayMap[map.weekday] ?? 0,
  };
}

/** "Apr 24" — short month + day in CET. */
export function formatDateShort(value: Date | string | number): string {
  const d = coerceDate(value);
  if (!d) return typeof value === 'string' ? value : '';
  return getFormatter({ month: 'short', day: 'numeric' }).format(d);
}

/** "Apr 24, 2026" — short month + day + year in CET. */
export function formatDateMedium(value: Date | string | number): string {
  const d = coerceDate(value);
  if (!d) return typeof value === 'string' ? value : '';
  return getFormatter({ month: 'short', day: 'numeric', year: 'numeric' }).format(d);
}

/** "Apr 24, 14:32" — medium date + 24h time in CET (no seconds, no label). */
export function formatDateTime(value: Date | string | number): string {
  const d = coerceDate(value);
  if (!d) return typeof value === 'string' ? value : '';
  return getFormatter({
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d).replace(',', '');
}

/** "Apr 24, 14:32 CET" — medium date + 24h time + CET label. */
export function formatDateTimeWithLabel(value: Date | string | number): string {
  const base = formatDateTime(value);
  if (!base) return base;
  return `${base} ${CET_LABEL}`;
}

/** "Apr 24, 2026, 14:32 CET" — full datetime + CET label. For detail pages. */
export function formatDateTimeFull(value: Date | string | number): string {
  const d = coerceDate(value);
  if (!d) return typeof value === 'string' ? value : '';
  const base = getFormatter({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return `${base} ${CET_LABEL}`;
}

/** "14:32" — 24h time only, no label. */
export function formatTimeOnly(value: Date | string | number): string {
  const d = coerceDate(value);
  if (!d) return typeof value === 'string' ? value : '';
  return getFormatter({
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/** "2026-04-24 14:32 CET" — ISO-ish date + time, fixed width for tables/cards. */
export function formatIsoLikeCet(value: Date | string | number): string {
  const parts = getCetParts(value);
  if (!parts) return typeof value === 'string' ? value : '';
  const mm = String(parts.month).padStart(2, '0');
  const dd = String(parts.day).padStart(2, '0');
  const hh = String(parts.hour).padStart(2, '0');
  const mi = String(parts.minute).padStart(2, '0');
  return `${parts.year}-${mm}-${dd} ${hh}:${mi} ${CET_LABEL}`;
}

/**
 * Relative time — "just now", "2m ago", "3h ago", "5d ago".
 * For older than 30 days, falls back to absolute `formatDateMedium`.
 */
export function formatRelativeCet(
  value: Date | string | number,
  now: Date | number = Date.now(),
): string {
  const d = coerceDate(value);
  if (!d) return typeof value === 'string' ? value : '';
  const nowMs = typeof now === 'number' ? now : now.getTime();
  const diff = Math.max(0, nowMs - d.getTime());
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return formatDateMedium(d);
}
